import {
  AsYouType,
  findPhoneNumbersInText,
  formatIncompletePhoneNumber,
  getCountryCallingCode,
  isPossiblePhoneNumber,
  isSupportedCountry,
  isValidPhoneNumber,
  Metadata,
  parsePhoneNumberWithError,
} from "libphonenumber-js";
import metadata from "libphonenumber-js/metadata.min.json";
import { countryTelephonyProviderMapping } from "@/src/constants/phoneNumberConstants.js";

export const parsePhone = (phoneNumberString, defaultCountry = null) => {
  try {
    if (!phoneNumberString) return null;
    return parsePhoneNumberWithError(phoneNumberString, defaultCountry);
  } catch (error) {
    return null;
  }
};

export const isValidPhone = (phoneNumberString, defaultCountry = null) => {
  try {
    return isValidPhoneNumber(phoneNumberString, defaultCountry);
  } catch (error) {
    return false;
  }
};

export const isPossiblePhone = (phoneNumberString, defaultCountry = null) => {
  try {
    return isPossiblePhoneNumber(phoneNumberString, defaultCountry);
  } catch (error) {
    return false;
  }
};

export const formatInternational = (
  phoneNumberString,
  defaultCountry = null,
) => {
  try {
    const phoneNumber = parsePhoneNumberWithError(
      phoneNumberString,
      defaultCountry,
    );
    return phoneNumber ? phoneNumber.formatInternational() : null;
  } catch (error) {
    return null;
  }
};

export const formatNational = (phoneNumberString, defaultCountry = null) => {
  try {
    const phoneNumber = parsePhoneNumberWithError(
      phoneNumberString,
      defaultCountry,
    );
    return phoneNumber ? phoneNumber.formatNational() : null;
  } catch (error) {
    return null;
  }
};

export const getCountryCode = (phoneNumberString, defaultCountry = null) => {
  try {
    const phoneNumber = parsePhoneNumberWithError(
      phoneNumberString,
      defaultCountry,
    );
    return phoneNumber ? phoneNumber.country : null;
  } catch (error) {
    return null;
  }
};

export const getCallingCode = (phoneNumberString, defaultCountry = null) => {
  try {
    const phoneNumber = parsePhoneNumberWithError(
      phoneNumberString,
      defaultCountry,
    );
    return phoneNumber ? phoneNumber.countryCallingCode : null;
  } catch (error) {
    return null;
  }
};

export const getCallingCodeByCountry = (countryCode) => {
  try {
    return getCountryCallingCode(countryCode);
  } catch (error) {
    return null;
  }
};

export const getNationalNumber = (phoneNumberString, defaultCountry = null) => {
  try {
    const phoneNumber = parsePhoneNumberWithError(
      phoneNumberString,
      defaultCountry,
    );
    return phoneNumber ? phoneNumber.nationalNumber : null;
  } catch (error) {
    return null;
  }
};

export const getPhoneNumberType = (
  phoneNumberString,
  defaultCountry = null,
) => {
  try {
    const phoneNumber = parsePhoneNumberWithError(
      phoneNumberString,
      defaultCountry,
    );
    return phoneNumber ? phoneNumber.getType() : null;
  } catch (error) {
    return null;
  }
};

export const formatAsYouType = (phoneNumberString, defaultCountry = null) => {
  try {
    const formatter = new AsYouType(defaultCountry);
    return formatter.input(phoneNumberString);
  } catch (error) {
    return phoneNumberString;
  }
};

export const formatIncomplete = (phoneNumberString, defaultCountry = null) => {
  try {
    return formatIncompletePhoneNumber(phoneNumberString, defaultCountry);
  } catch (error) {
    return phoneNumberString;
  }
};

export const findPhoneNumbers = (text, defaultCountry = null) => {
  try {
    return findPhoneNumbersInText(text, defaultCountry);
  } catch (error) {
    return [];
  }
};

export const extractPhoneNumbers = (text, defaultCountry = null) => {
  try {
    const phoneNumbers = findPhoneNumbersInText(text, defaultCountry);
    return phoneNumbers.map((phoneNumber) => ({
      number: phoneNumber.number.formatInternational(),
      country: phoneNumber.number.country,
      type: phoneNumber.number.getType(),
      startsAt: phoneNumber.startsAt,
      endsAt: phoneNumber.endsAt,
    }));
  } catch (error) {
    return [];
  }
};

export const isCountrySupported = (countryCode) => {
  try {
    return isSupportedCountry(countryCode);
  } catch (error) {
    return false;
  }
};

export const getPossibleCountries = (phoneNumberString) => {
  try {
    const phoneNumber = parsePhoneNumberWithError(phoneNumberString);
    return phoneNumber ? phoneNumber.getPossibleCountries() : [];
  } catch (error) {
    return [];
  }
};

export const isPhoneEqual = (
  phoneNumber1,
  phoneNumber2,
  defaultCountry = null,
) => {
  try {
    const parsed1 = parsePhoneNumberWithError(phoneNumber1, defaultCountry);
    const parsed2 = parsePhoneNumberWithError(phoneNumber2, defaultCountry);

    if (!parsed1 || !parsed2) return false;

    return parsed1.number === parsed2.number;
  } catch (error) {
    return false;
  }
};

export const getPhoneNumberInfo = (
  phoneNumberString,
  defaultCountry = null,
) => {
  try {
    const phoneNumber = parsePhoneNumberWithError(
      phoneNumberString,
      defaultCountry,
    );

    if (!phoneNumber) return null;

    return {
      isValid: phoneNumber.isValid(),
      isPossible: phoneNumber.isPossible(),
      country: phoneNumber.country,
      countryCallingCode: phoneNumber.countryCallingCode,
      nationalNumber: phoneNumber.nationalNumber,
      type: phoneNumber.getType(),
      formatted: {
        international: phoneNumber.formatInternational(),
        national: phoneNumber.formatNational(),
      },
    };
  } catch (error) {
    return null;
  }
};

export const cleanPhoneNumber = (phoneNumberString) => {
  if (!phoneNumberString) return "";
  return phoneNumberString.replace(/[^\d+]/g, "");
};

export const validateAndFormat = (
  phoneNumberString,
  defaultCountry = null,
  format = "INTERNATIONAL",
) => {
  try {
    if (!isValidPhone(phoneNumberString, defaultCountry)) {
      return {
        isValid: false,
        formatted: null,
        error: "Invalid phone number",
      };
    }

    const phoneNumber = parsePhoneNumberWithError(
      phoneNumberString,
      defaultCountry,
    );
    let formatted;

    switch (format.toUpperCase()) {
      case "NATIONAL":
        formatted = phoneNumber.formatNational();
        break;
      case "INTERNATIONAL":
      default:
        formatted = phoneNumber.formatInternational();
        break;
    }

    return {
      isValid: true,
      formatted,
      country: phoneNumber.country,
      type: phoneNumber.getType(),
    };
  } catch (error) {
    return {
      isValid: false,
      formatted: null,
      error: error.message,
    };
  }
};

export const getCountriesByProvider = (provider) => {
  try {
    return Object.keys(countryTelephonyProviderMapping).filter(
      (country) => countryTelephonyProviderMapping[country] === provider,
    );
  } catch (error) {
    console.error(
      "An error occurred while getting countries by provider: ",
      error,
    );
    return [];
  }
};

export const normalizePhoneNumber = (number) => {
  const str = String(number);
  return str.startsWith("+") ? str : `+${str}`;
};

export const getCountryCodeFromDialCode = (dialCode, defaultCountry = "US") => {
  try {
    if (!dialCode) return defaultCountry;
    const normalized = dialCode.startsWith("+") ? dialCode.slice(1) : dialCode;
    const metadataInstance = new Metadata(metadata);
    const countryCodes = metadataInstance.getCountryCodesForCallingCode(normalized);
    return countryCodes?.[0] || defaultCountry;
  } catch (error) {
    return defaultCountry;
  }
};
